import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CATEGORIES, type Category } from "../../stock/catalog";
import type { Status, StockItem } from "../../stock/model";
import type { StockAnswer } from "../../stock/sheet";
import { api } from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import Icon from "../components/Icon";
import { LastsBar, MiniBars } from "./charts";
import ItemPanel from "./ItemPanel";
import StockGrid, { type Column, type Group, type Sort } from "./StockGrid";
import { agoText, clock, dayDate, daysAgo, fmt, shopColor, shopShort, shortDate, STATUS, weekNumber } from "./look";

// The Inventory tool (Viktor, 31 Oct 2026: "create new Inventory module that uses this sheet as database … a great
// warehouse web app that has the functionality that these sheets (many sheets) are trying to do … master detail grid
// and filters above for kitchen and different stores"). One screen instead of the sheet's sixteen tabs: pick the
// warehouse, the kitchen, all shops or one shop above, and the grid shows what matters there; an item opens beside
// the grid (over it on a phone) with its counts, the shops' requests and when to order. Nothing is written to the
// sheet; the shops and the warehouse keep using their Google Forms.

type Place = "warehouse" | "kitchen" | "shops" | `shop:${string}`;
const PLACE_KEY = "stock.place";
const loadPlace = (): Place => {
  try {
    const saved = localStorage.getItem(PLACE_KEY);
    if (saved === "warehouse" || saved === "kitchen" || saved === "shops" || saved?.startsWith("shop:")) return saved as Place;
  } catch {
    /* private window */
  }
  return "warehouse";
};

const WIDE = "(min-width: 1024px)";
function useWide() {
  const [wide, setWide] = useState(() => matchMedia(WIDE).matches);
  useEffect(() => {
    const query = matchMedia(WIDE);
    const change = () => setWide(query.matches);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);
  return wide;
}

// Ticks for what has been packed for a shop in a week. They are kept on this phone or computer only: the sheet's own
// "Packed" boxes are not written to.
function usePacked(shop: string | null, week: string) {
  const key = shop ? `stock.packed.${shop}.${week}` : null;
  const [packed, setPacked] = useState<Set<string>>(new Set());
  useEffect(() => {
    try {
      setPacked(new Set(key ? (JSON.parse(localStorage.getItem(key) ?? "[]") as string[]) : []));
    } catch {
      setPacked(new Set());
    }
  }, [key]);
  const toggle = (id: string) =>
    setPacked((old) => {
      const next = new Set(old);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        if (key) localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        /* kept until the page is left */
      }
      return next;
    });
  return [packed, toggle] as const;
}

function Tile({ label, value, note, tone, active, onClick }: { label: string; value: ReactNode; note?: ReactNode; tone?: Status; active?: boolean; onClick?: () => void }) {
  const look = tone ? STATUS[tone] : null;
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">
        {look && <span className={`flex size-4 items-center justify-center rounded-full text-[10px] ${look.pill}`}>{look.mark}</span>}
        {label}
      </span>
      <span className="mt-0.5 block text-[24px] font-bold leading-tight tabular-nums">{value}</span>
      {note && <span className="block truncate text-[12px] text-ink-soft">{note}</span>}
    </>
  );
  const frame = `min-w-0 rounded-[18px] border bg-white px-4 py-3 text-left shadow-card ${active ? "border-rose ring-2 ring-rose/25" : "border-line-soft"}`;
  return onClick ? (
    <button onClick={onClick} aria-pressed={active} className={`${frame} transition active:scale-[0.98]`}>
      {body}
    </button>
  ) : (
    <div className={frame}>{body}</div>
  );
}

function Chip({ active, onClick, children, dot }: { active: boolean; onClick: () => void; children: ReactNode; dot?: string }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[14px] font-bold transition ${
        active ? "bg-rose-grad text-white shadow-button" : "border border-line bg-white text-ink-soft hover:border-[#e6c4cb]"
      }`}
    >
      {dot && <span className="size-2.5 rounded-full ring-2 ring-white" style={{ background: dot }} />}
      {children}
    </button>
  );
}

function StatusPill({ status }: { status: Status }) {
  const look = STATUS[status];
  return (
    <span title={look.label} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[12px] font-bold ${look.pill}`}>
      <span aria-hidden="true">{look.mark}</span>
      <span className="@xl:hidden">{look.short}</span>
      <span className="hidden @xl:inline">{look.label}</span>
    </span>
  );
}

const readTime = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });

export default function StockApp({ onHome }: { onHome: () => void }) {
  const [data, setData] = useState<StockAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [place, setPlaceState] = useState<Place>(loadPlace);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category | null>(null);
  const [focus, setFocus] = useState<Status | "short" | null>(null);
  const [weekPick, setWeek] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>(null);
  const [everything, setEverything] = useState(false);
  const pushed = useRef(false);
  const wide = useWide();

  const load = (fresh = false) => {
    setLoading(true);
    api
      .stock(fresh)
      .then((answer) => {
        setData(answer);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  const shop = place.startsWith("shop:") ? place.slice(5) : null;
  const week = weekPick ?? data?.currentWeek ?? "";
  // A changed request comes back with the whole stock worked out again, which replaces what is shown.
  const saveRequest = (itemId: string) => async (forShop: string, amount: number) => {
    setData(await api.stockRequest({ shop: forShop, week, itemId, amount }));
  };
  const [packed, togglePacked] = usePacked(shop, week);

  const setPlace = (next: Place) => {
    setPlaceState(next);
    setFocus(null);
    setSort(null);
    setEverything(false);
    try {
      localStorage.setItem(PLACE_KEY, next);
    } catch {
      /* private window */
    }
  };

  const select = (item: StockItem) => {
    if (wide) {
      setSelectedId(item.id === selectedId ? null : item.id);
      return;
    }
    // On a phone the item opens over the list, and the back button closes it.
    if (!pushed.current) {
      pushed.current = true;
      pushScreen(() => {
        pushed.current = false;
        setSelectedId(null);
      });
    }
    setSelectedId(item.id);
  };
  const close = () => (pushed.current ? goBack() : setSelectedId(null));

  const view = useMemo(() => {
    if (!data) return null;
    const asked = (it: StockItem, s: string, w: string) => it.weekly[s]?.[w] ?? 0;
    const notes = (it: StockItem, s: string, w: string) => it.notes[s]?.[w] ?? [];
    const weekTotal = (it: StockItem, w: string) => data.shops.reduce((t, s) => t + asked(it, s, w), 0);
    const anyAsked = (it: StockItem, s: string, w: string) => asked(it, s, w) > 0 || notes(it, s, w).length > 0;
    const rateWeeks = data.weeks.filter((w) => w < data.currentWeek).slice(-12);
    const trendWeeks = data.weeks.filter((w) => w <= week).slice(-12);
    const year = week.slice(0, 4);
    const sentAt = (s: string) => data.submissions[s]?.[week] ?? [];

    const amount = (it: StockItem, s: string, w: string) => {
      const n = asked(it, s, w);
      const words = notes(it, s, w);
      const changed = it.changed[s]?.[w];
      const hint = changed ? `Changed in the app; the sheet says ${fmt(changed.sheet)}` : undefined;
      if (!n && !words.length) return changed ? <span className="text-ink-faint" title={hint}>0<span className="ml-0.5 text-rose">•</span></span> : <span className="text-ink-faint">·</span>;
      return (
        <span className="font-semibold tabular-nums" title={hint}>
          {[n ? fmt(n) : "", ...words].filter(Boolean).join(" + ")}
          {changed && <span className="ml-0.5 text-rose">•</span>}
        </span>
      );
    };
    const itemColumn = (sub?: (it: StockItem) => ReactNode): Column => ({
      key: "item",
      label: "Item",
      // The item takes the spare width, so the figures sit together on the right.
      className: "w-full",
      sort: (it) => it.name.toLowerCase(),
      render: (it) => (
        <div className="min-w-[7.5rem]">
          <div className="font-semibold leading-tight">{it.name}</div>
          {sub && <div className="text-[12px] leading-tight text-ink-faint">{sub(it)}</div>}
        </div>
      ),
    });
    const trendColumn = (shops: string[], className: string): Column => ({
      key: "trend",
      label: "Last 12 weeks",
      className,
      render: (it) => {
        const values = trendWeeks.map((w) => shops.reduce((t, s) => t + asked(it, s, w), 0));
        return <MiniBars values={values} labels={trendWeeks.map((w) => `Week ${weekNumber(w)}`)} max={Math.max(...values)} />;
      },
    });
    const perWeek = (it: StockItem, shops: string[]) => rateWeeks.reduce((t, w) => t + shops.reduce((u, s) => u + asked(it, s, w), 0), 0) / Math.max(1, rateWeeks.length);

    let items: StockItem[];
    let columns: Column[];
    let grouping: "category" | "packing";
    let defaultSort: Sort;
    let tiles: ReactNode;
    // What the amounts in the grid are: a shop's requests are what it needs, not what it has (Viktor, 31 Oct 2026).
    let note: string | null = null;

    if (place === "warehouse") {
      items = data.items.filter((it) => it.warehouse || (it.packedBy === "warehouse" && it.perWeek));
      const lead = (it: StockItem) => (it.orderDays === null && it.productionDays === null ? null : ((it.orderDays ?? 0) + (it.productionDays ?? 0)) / 7);
      columns = [
        itemColumn((it) => <span className="@4xl:hidden">{it.supplier}</span>),
        { key: "supplier", label: "Supplier", className: "hidden @4xl:table-cell", sort: (it) => it.supplier ?? "", render: (it) => <span className="text-ink-soft">{it.supplier ?? "–"}</span> },
        {
          key: "counted",
          label: "Counted",
          title: `The latest warehouse count (${shortDate(data.warehouseCountedAt)})`,
          align: "right",
          className: "hidden @2xl:table-cell",
          sort: (it) => it.warehouse?.last ?? null,
          render: (it) =>
            it.warehouse ? (
              <span className="tabular-nums" title={`Counted ${shortDate(it.warehouse.at)}`}>
                {fmt(it.warehouse.last)}
                {it.warehouse.at?.slice(0, 10) !== data.warehouseCountedAt?.slice(0, 10) && <span className="ml-1 text-[11px] text-ink-faint">{shortDate(it.warehouse.at)}</span>}
              </span>
            ) : (
              <span className="text-ink-faint">–</span>
            ),
        },
        { key: "now", label: "Today ≈", title: "The last count less what the shops requested since", align: "right", sort: (it) => it.nowEstimate, render: (it) => <span className="font-bold tabular-nums">{fmt(it.nowEstimate)}</span> },
        { key: "supplierBoxes", label: "At supplier", title: "Boxes the supplier keeps for us", align: "right", className: "hidden @5xl:table-cell", sort: (it) => it.supplierBoxes, render: (it) => <span className="tabular-nums text-ink-soft">{fmt(it.supplierBoxes)}</span> },
        { key: "perWeek", label: "Per week", title: "Requested by the shops, last 12 weeks", align: "right", className: "hidden @2xl:table-cell", sort: (it) => it.perWeek, render: (it) => <span className="tabular-nums">{fmt(it.perWeek)}</span> },
        { key: "lasts", label: "Lasts, weeks", title: "With the supplier's stock; the tick is the time to get more made", className: "hidden @lg:table-cell", sort: (it) => it.weeksLeft, render: (it) => <LastsBar weeks={it.weeksLeft} leadWeeks={lead(it)} status={it.status} /> },
        {
          key: "orderBy",
          label: "Order by",
          className: "hidden @3xl:table-cell",
          sort: (it) => it.orderBy,
          render: (it) =>
            it.orderBy ? (
              <span className="whitespace-nowrap">
                <span className="font-semibold">{it.orderBy <= data.today ? "Now" : shortDate(it.orderBy)}</span>
                <span className="ml-1.5 text-[12px] text-ink-faint">{it.orderKind === "call-off" ? "call off" : it.orderKind === "batch" ? "new batch" : ""}</span>
              </span>
            ) : (
              <span className="text-ink-faint">–</span>
            ),
        },
        { key: "status", label: "Status", sort: (it) => `${STATUS[it.status].rank}-${it.orderBy ?? "9999"}`, render: (it) => <StatusPill status={it.status} /> },
      ];
      grouping = "category";
      defaultSort = { key: "status", dir: 1 };
      const count = (s: Status) => items.filter((it) => it.status === s).length;
      const shopsIn = data.shops.filter((s) => data.submissions[s]?.[data.currentWeek]);
      tiles = (
        <>
          <Tile label="Order now" value={count("order")} note="runs out before a delivery" tone="order" active={focus === "order"} onClick={() => setFocus(focus === "order" ? null : "order")} />
          <Tile label="Order soon" value={count("soon")} note="within two weeks" tone="soon" active={focus === "soon"} onClick={() => setFocus(focus === "soon" ? null : "soon")} />
          <Tile label="Last count" value={shortDate(data.warehouseCountedAt)} note={data.warehouseCountedAt ? agoText(daysAgo(data.warehouseCountedAt, data.today)) : "never"} />
          <Tile label={`Requests, week ${weekNumber(data.currentWeek)}`} value={`${shopsIn.length} of ${data.shops.length}`} note={shopsIn.length === data.shops.length ? "every shop has asked" : `waiting for ${data.shops.filter((s) => !shopsIn.includes(s)).map(shopShort).join(", ")}`} onClick={() => setPlace("shops")} />
        </>
      );
    } else if (place === "kitchen") {
      items = data.items.filter((it) => it.kitchen || it.packedBy === "kitchen");
      columns = [
        itemColumn((it) => it.supplier),
        {
          key: "kitchen",
          label: "In kitchen",
          align: "right",
          sort: (it) => it.kitchen?.last ?? null,
          render: (it) => (it.kitchen ? <span className="tabular-nums">{fmt(it.kitchen.last)}</span> : <span className="text-ink-faint">–</span>),
        },
        { key: "supplierBoxes", label: "At supplier", align: "right", className: "hidden @lg:table-cell", sort: (it) => it.supplierBoxes, render: (it) => <span className="tabular-nums text-ink-soft">{fmt(it.supplierBoxes)}</span> },
        { key: "week", label: "Requested", title: `What the shops requested from the kitchen for week ${weekNumber(week)}`, align: "right", sort: (it) => weekTotal(it, week), render: (it) => (weekTotal(it, week) || data.shops.some((s) => notes(it, s, week).length) ? <span className="font-bold tabular-nums">{[weekTotal(it, week) ? fmt(weekTotal(it, week)) : "", ...data.shops.flatMap((s) => notes(it, s, week))].filter(Boolean).join(" + ")}</span> : <span className="text-ink-faint">·</span>) },
        { key: "perWeek", label: "Per week", align: "right", className: "hidden @xl:table-cell", sort: (it) => it.perWeek, render: (it) => <span className="tabular-nums">{fmt(it.perWeek)}</span> },
        trendColumn(data.shops, "hidden @2xl:table-cell"),
      ];
      grouping = "category";
      defaultSort = { key: "item", dir: 1 };
      const askedNow = items.filter((it) => it.packedBy === "kitchen" && weekTotal(it, week) > 0);
      note = `Requested is what the shops asked the kitchen for in week ${weekNumber(week)}; In kitchen is the kitchen's own count.`;
      tiles = (
        <>
          <Tile label="Kitchen count" value={shortDate(data.kitchenCountedAt)} note={data.kitchenCountedAt ? agoText(daysAgo(data.kitchenCountedAt, data.today)) : "never"} tone={data.kitchenCountedAt && daysAgo(data.kitchenCountedAt, data.today) > 30 ? "soon" : undefined} />
          <Tile label={`To pack, week ${weekNumber(week)}`} value={askedNow.length} note="items the shops requested from the kitchen" />
          <Tile label="Packed by the kitchen" value={items.filter((it) => it.packedBy === "kitchen").length} note="items on the shops' form" />
          <Tile label="Kept in the kitchen" value={items.filter((it) => it.kitchen).length} note="items on the kitchen count" />
        </>
      );
    } else if (place === "shops") {
      items = data.items.filter((it) => data.shops.some((s) => anyAsked(it, s, week)));
      columns = [
        itemColumn(),
        ...data.shops.map(
          (s): Column => ({
            key: `shop:${s}`,
            label: (
              <span className="inline-flex flex-col items-end gap-0.5">
                <span className="inline-flex items-center gap-1">
                  <span className="size-2 rounded-full" style={{ background: shopColor(data.shops, s) }} />
                  {shopShort(s)}
                </span>
                <span className="text-[10px] font-semibold normal-case tracking-normal">{sentAt(s).length ? `${dayDate(sentAt(s)[0]!).slice(0, 3)} ${clock(sentAt(s)[0]!)}` : "not sent"}</span>
              </span>
            ),
            title: s,
            align: "right",
            sort: (it) => asked(it, s, week),
            render: (it) => amount(it, s, week),
          }),
        ),
        { key: "total", label: "Total", align: "right", sort: (it) => weekTotal(it, week), render: (it) => <span className="font-bold tabular-nums">{fmt(weekTotal(it, week))}</span> },
        {
          key: "have",
          label: "Warehouse ≈",
          title: "In the warehouse today: the last count less what the shops requested since",
          align: "right",
          className: "hidden @2xl:table-cell",
          sort: (it) => it.nowEstimate,
          render: (it) =>
            it.packedBy === "kitchen" ? (
              <span className="text-[12px] text-ink-faint">kitchen</span>
            ) : it.short > 0 ? (
              <span className="font-bold text-[#9b2121]" title={`The shops requested ${fmt(it.short)} more than the count had`}>short {fmt(it.short)}</span>
            ) : (
              <span className="tabular-nums text-ink-soft">{fmt(it.nowEstimate)}</span>
            ),
        },
      ];
      grouping = "packing";
      defaultSort = { key: "item", dir: 1 };
      const shopsIn = data.shops.filter((s) => sentAt(s).length);
      const short = items.filter((it) => it.packedBy === "warehouse" && it.short > 0);
      note = `The amounts are what each shop requested for week ${weekNumber(week)}: what it needs, not its stock. Warehouse ≈ is the warehouse's own stock.`;
      tiles = (
        <>
          <Tile label={`Requests, week ${weekNumber(week)}`} value={`${shopsIn.length} of ${data.shops.length}`} note={shopsIn.length === data.shops.length ? "every shop has asked" : `waiting for ${data.shops.filter((s) => !shopsIn.includes(s)).map(shopShort).join(", ")}`} />
          <Tile label="Items requested" value={items.length} note={`${items.filter((it) => it.packedBy === "warehouse").length} from the warehouse`} />
          <Tile label="From the kitchen" value={items.filter((it) => it.packedBy === "kitchen").length} note="milk, syrups, cakes and more" />
          <Tile label="Short in warehouse" value={short.length} note="requested more than counted" tone={short.length ? "order" : "ok"} active={focus === "short"} onClick={() => setFocus(focus === "short" ? null : "short")} />
        </>
      );
    } else {
      const s = shop!;
      items = data.items.filter((it) => anyAsked(it, s, week) || (everything && (it.weekly[s] || it.notes[s])));
      columns = [
        {
          key: "packed",
          label: <span className="sr-only">Packed</span>,
          className: "w-10",
          render: (it) =>
            anyAsked(it, s, week) ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePacked(it.id);
                }}
                aria-pressed={packed.has(it.id)}
                aria-label={`Packed: ${it.name}`}
                className={`flex size-7 items-center justify-center rounded-lg border-2 text-[14px] font-bold transition ${packed.has(it.id) ? "border-rose bg-rose text-white" : "border-line bg-white text-transparent hover:border-rose"}`}
              >
                ✓
              </button>
            ) : null,
        },
        itemColumn((it) => it.category),
        { key: "week", label: "Requested", title: `What the shop requested for week ${weekNumber(week)}: what it needs, not what it has in stock`, align: "right", sort: (it) => asked(it, s, week), render: (it) => <span className={packed.has(it.id) ? "text-ink-faint line-through" : ""}>{amount(it, s, week)}</span> },
        trendColumn([s], "hidden @lg:table-cell"),
        { key: "perWeek", label: "Per week", align: "right", className: "hidden @xl:table-cell", sort: (it) => perWeek(it, [s]), render: (it) => <span className="tabular-nums">{fmt(perWeek(it, [s]))}</span> },
        { key: "year", label: year, align: "right", className: "hidden @2xl:table-cell", sort: (it) => Object.entries(it.weekly[s] ?? {}).reduce((t, [w, n]) => t + (w.startsWith(year) ? n : 0), 0), render: (it) => <span className="tabular-nums text-ink-soft">{fmt(Object.entries(it.weekly[s] ?? {}).reduce((t, [w, n]) => t + (w.startsWith(year) ? n : 0), 0))}</span> },
      ];
      grouping = "packing";
      defaultSort = { key: "item", dir: 1 };
      const toPack = items.filter((it) => anyAsked(it, s, week));
      note = `The amounts are what ${s} requested for week ${weekNumber(week)}: what it needs, not what it has in stock.`;
      const done = toPack.filter((it) => packed.has(it.id)).length;
      tiles = (
        <>
          <Tile label="Request" value={sentAt(s).length ? dayDate(sentAt(s)[0]!) : "Not sent"} note={sentAt(s).length ? `at ${sentAt(s).map(clock).join(" and ")}` : `week ${weekNumber(week)}`} tone={sentAt(s).length ? undefined : "soon"} />
          <Tile label="Items requested" value={toPack.length} note={`${toPack.filter((it) => it.packedBy === "kitchen").length} from the kitchen`} />
          <Tile label="Packed" value={`${done} of ${toPack.length}`} note={done === toPack.length && toPack.length ? "all packed" : "ticked on this device"} tone={done === toPack.length && toPack.length ? "ok" : undefined} />
          <Tile label={`Requests in ${year}`} value={Object.keys(data.submissions[s] ?? {}).filter((w) => w.startsWith(year)).length} note="weekly requests" />
        </>
      );
    }

    // Filters: search, kind of item, and the tile picked.
    const words = query.trim().toLowerCase();
    const shown = items.filter(
      (it) =>
        (!words || it.name.toLowerCase().includes(words) || it.supplier?.toLowerCase().includes(words)) &&
        (!category || it.category === category) &&
        (!focus || (focus === "short" ? it.short > 0 && it.packedBy === "warehouse" : it.status === focus)),
    );
    const groups: Group[] =
      grouping === "category"
        ? CATEGORIES.map((c) => ({ key: c, label: c, items: shown.filter((it) => it.category === c) }))
        : [
            { key: "warehouse", label: "From the warehouse", items: shown.filter((it) => it.packedBy === "warehouse") },
            { key: "kitchen", label: "From the kitchen", items: shown.filter((it) => it.packedBy === "kitchen") },
          ].map((g) => (shop ? { ...g, note: `${g.items.filter((it) => packed.has(it.id)).length} of ${g.items.filter((it) => anyAsked(it, shop, week)).length} packed` } : g));
    const categories = CATEGORIES.filter((c) => items.some((it) => it.category === c));
    return { columns, groups, defaultSort, tiles, categories, total: items.length, note };
  }, [data, place, week, query, category, focus, everything, packed, shop]);

  const selected = data?.items.find((it) => it.id === selectedId) ?? null;
  const weekIndex = data ? data.weeks.indexOf(week) : -1;
  const live = data?.source.kind === "sheet";
  const sheetLink = data ? `https://docs.google.com/spreadsheets/d/${data.source.sheetId}/edit` : "#";

  const findings = data && data.issues.length > 0 && (
    <section className="rounded-[20px] border border-line-soft bg-white p-4 shadow-card">
      <h2 className="text-[15px] font-bold">What the sheet misses</h2>
      <p className="mt-0.5 text-[12px] text-ink-faint">Found while reading the tabs. The app works around these; the sheet's own tabs do not.</p>
      <ul className="mt-3 flex flex-col gap-2">
        {data.issues.map((issue) => (
          <li key={issue} className="flex gap-2 text-[13px] leading-snug text-ink-soft">
            <span className="mt-[3px] flex size-4 shrink-0 items-center justify-center rounded-full bg-[#fdf0d2] text-[10px] text-[#7a5200]">◷</span>
            {issue}
          </li>
        ))}
      </ul>
    </section>
  );

  return (
    <div className="mx-auto flex min-h-dvh max-w-[1480px] flex-col gap-4 px-4 pb-16 pt-4 lg:px-6">
      <BackHeader
        title="Inventory"
        logo
        onBack={onHome}
        action={
          <button
            onClick={() => load(true)}
            disabled={loading}
            aria-label="Read the sheet again"
            title="Read the sheet again"
            className="flex size-10 shrink-0 items-center justify-center rounded-full border border-line bg-white text-rose-ink transition active:scale-95 disabled:opacity-60"
          >
            <Icon name="refresh" size={20} className={loading ? "animate-spin" : ""} />
          </button>
        }
      />

      {data && (
        <div className="-mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-faint">
          <span className="flex items-center gap-1.5">
            <span className={`size-2 rounded-full ${live ? "bg-[#0ca30c]" : "bg-[#fab219]"}`} />
            {live ? `Live from the sheet · read ${readTime.format(new Date(data.source.readAt))}` : `Saved copy of the sheet from ${readTime.format(new Date(data.source.readAt))}`}
          </span>
          <a href={sheetLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-rose-deep">
            {data.title} <Icon name="external" size={12} />
          </a>
        </div>
      )}

      {error && (
        <div className="rounded-2xl bg-[#fbe4e4] px-4 py-3 text-[14px] text-[#9b2121]">
          {error}{" "}
          <button className="font-bold underline" onClick={() => load(true)}>
            Try again
          </button>
        </div>
      )}

      {!data && !error && <div className="py-20 text-center text-ink-faint">Reading the stock sheet…</div>}

      {data && view && (
        <>
          <nav aria-label="Place" className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 lg:mx-0 lg:flex-wrap lg:px-0">
            <Chip active={place === "warehouse"} onClick={() => setPlace("warehouse")}>Warehouse</Chip>
            <Chip active={place === "kitchen"} onClick={() => setPlace("kitchen")}>Kitchen</Chip>
            <Chip active={place === "shops"} onClick={() => setPlace("shops")}>All shops</Chip>
            {data.shops.map((s) => (
              <Chip key={s} active={shop === s} onClick={() => setPlace(`shop:${s}`)} dot={shopColor(data.shops, s)}>
                {s}
              </Chip>
            ))}
          </nav>

          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">{view.tiles}</div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-0 flex-1 basis-56 items-center gap-2 rounded-full border border-line bg-white px-3.5 py-2 shadow-sm focus-within:border-rose">
              <Icon name="search" size={18} className="shrink-0 text-ink-faint" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find an item or supplier"
                className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-faint"
              />
            </label>
            {place !== "warehouse" && (
              <div className="flex items-center rounded-full border border-line bg-white shadow-sm">
                <button onClick={() => setWeek(data.weeks[weekIndex - 1]!)} disabled={weekIndex <= 0} aria-label="Week before" className="flex size-10 items-center justify-center text-rose-ink disabled:opacity-30">
                  <Icon name="back" size={18} />
                </button>
                <span className="min-w-[8.5rem] text-center text-[14px] font-bold">
                  Week {weekNumber(week)} <span className="font-semibold text-ink-faint">· {shortDate(week)}</span>
                </span>
                <button onClick={() => setWeek(data.weeks[weekIndex + 1]!)} disabled={weekIndex >= data.weeks.length - 1} aria-label="Week after" className="flex size-10 items-center justify-center text-rose-ink disabled:opacity-30">
                  <Icon name="chevronRight" size={18} />
                </button>
              </div>
            )}
            {shop && (
              <button onClick={() => setEverything(!everything)} aria-pressed={everything} className={`rounded-full px-3.5 py-2 text-[13px] font-bold ${everything ? "bg-blush text-rose-ink" : "border border-line bg-white text-ink-soft"}`}>
                {everything ? "Showing all it has requested" : "Only this week"}
              </button>
            )}
          </div>
          <div className="no-scrollbar -mx-4 -mt-1 flex gap-1.5 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
            <button onClick={() => setCategory(null)} className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-semibold ${category === null ? "bg-[#3a1c25] text-white" : "bg-white/70 text-ink-soft"}`}>
              All · {view.total}
            </button>
            {view.categories.map((c) => (
              <button key={c} onClick={() => setCategory(category === c ? null : c)} className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-semibold ${category === c ? "bg-[#3a1c25] text-white" : "bg-white/70 text-ink-soft"}`}>
                {c}
              </button>
            ))}
          </div>

          {view.note && (
            <p className="-mt-1 flex items-start gap-2 text-[13px] leading-snug text-ink-soft">
              <span aria-hidden className="mt-[3px] size-2 shrink-0 rounded-full bg-rose" />
              {view.note}
            </p>
          )}

          <div className={selected && wide ? "grid grid-cols-[minmax(0,1fr)_380px] items-start gap-5 xl:grid-cols-[minmax(0,1fr)_420px]" : ""}>
            <StockGrid
              columns={view.columns}
              groups={view.groups}
              selectedId={selectedId}
              onSelect={select}
              sort={sort ?? view.defaultSort}
              onSort={(key) => setSort((old) => {
                const current = old ?? view.defaultSort;
                return current?.key === key ? { key, dir: current.dir === 1 ? -1 : 1 } : { key, dir: 1 };
              })}
              empty={view.total ? "Nothing matches the filters." : place === "warehouse" ? "Nothing counted yet." : "No requests this week yet."}
            />
            {wide && selected && (
              <aside className="sticky top-4 max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-[20px] border border-line-soft bg-white p-4 shadow-card">
                <ItemPanel item={selected} data={data} onClose={close} shop={shop} week={week} save={saveRequest(selected.id)} />
              </aside>
            )}
          </div>
          {findings}
        </>
      )}

      {!wide && selected && data && (
        <div className="fixed inset-0 z-30 overflow-y-auto bg-[#fdf6f6] px-4 pb-10 pt-4">
          <ItemPanel item={selected} data={data} onClose={close} shop={shop} week={week} save={saveRequest(selected.id)} />
        </div>
      )}
    </div>
  );
}
